import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Layers2, MapPin, Package, Users } from 'lucide-react';
import './Dashboard.css';
import DashboardAppBar from '../components/DashboardAppBar';
import { Badge } from 'components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { getAllPurchaseOrders } from '../services/purchaseOrdersService';
import {
  loadMaterialInventoryRows,
  summarizeMaterialRowsByLot
} from '../services/materialInventoryAggregation';
import { normalizeSupplierNameForMatching, displayNameForCanonicalSupplier } from '../utils/supplierNameNormalize';
import { useAuthSession } from '../contexts/AuthSessionContext';
import { getLockedLotFilter } from '../services/authService';
import { SHOW_MATERIAL_INVENTORY } from '../config/featureFlags';

function emptyBuckets() {
  return {
    poles: { poAmount: 0, invoiced: 0, count: 0 },
    cables: { poAmount: 0, invoiced: 0, count: 0 },
    fittings: { poAmount: 0, invoiced: 0, count: 0 },
    transformers: { poAmount: 0, invoiced: 0, count: 0 },
    meters: { poAmount: 0, invoiced: 0, count: 0 },
    others: { poAmount: 0, invoiced: 0, count: 0 },
    fleet: { poAmount: 0, invoiced: 0, count: 0 },
    transportation: { poAmount: 0, invoiced: 0, count: 0 }
  };
}

function rawPoAmount(po) {
  const r = po._raw || {};
  const rawPo = Number(r.poAmount) || 0;
  const rawTotal = Number(r.totalAmount) || 0;
  return rawPo > 0 ? rawPo : rawTotal;
}

function rawInvoiced(po) {
  return Number((po._raw || {}).invoicedAmount) || 0;
}

/** Map PO to KPI bucket key; null = excluded from top KPI row */
function bucketForPo(po) {
  const cat = (po.category || '').toLowerCase().trim();
  const sub = (po.subcategory || '').toLowerCase().trim();

  if (cat === 'others') return 'others';

  if (cat === 'fleet') return 'fleet';

  if (cat === 'transportation') return 'transportation';

  if (cat !== 'material') return null;

  if (
    sub === 'poles' ||
    sub === 'pole' ||
    sub === 'wooden poles' ||
    sub === 'wooden pole'
  ) {
    return 'poles';
  }
  if (sub === 'cables' || sub === 'cable') return 'cables';
  if (sub === 'fittings' || sub === 'fitting') return 'fittings';
  if (sub === 'transformers' || sub === 'transformer') return 'transformers';
  if (sub === 'meters' || sub === 'meter') return 'meters';

  return null;
}

function isLot8(lot) {
  const s = (lot || '').toLowerCase();
  return s.includes('lot 8') || s.includes('lot8');
}

function isLot10(lot) {
  const s = (lot || '').toLowerCase();
  return s.includes('lot 10') || s.includes('lot10');
}

/** Full amount with comma separators (no M / K shorthand). */
function formatCurrencyLong(value) {
  const n = Number(value) || 0;
  const formatted = n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  return `TZS ${formatted}`;
}

function aggregatePurchaseOrders(pos) {
  const global = emptyBuckets();
  const lot8 = emptyBuckets();
  const lot10 = emptyBuckets();

  for (const po of pos) {
    const bucket = bucketForPo(po);
    if (!bucket) continue;

    const amt = rawPoAmount(po);
    const inv = rawInvoiced(po);

    global[bucket].poAmount += amt;
    global[bucket].invoiced += inv;
    global[bucket].count += 1;

    const lot = po.lot || '';
    if (isLot8(lot)) {
      lot8[bucket].poAmount += amt;
      lot8[bucket].invoiced += inv;
      lot8[bucket].count += 1;
    } else if (isLot10(lot)) {
      lot10[bucket].poAmount += amt;
      lot10[bucket].invoiced += inv;
      lot10[bucket].count += 1;
    }
  }

  return { global, lot8, lot10 };
}

/** Sum PO amount & invoiced across every fetched PO (not only category buckets). */
function aggregateAllPoTotals(pos) {
  let totalPoAmount = 0;
  let totalInvoiced = 0;
  for (const po of pos) {
    totalPoAmount += rawPoAmount(po);
    totalInvoiced += rawInvoiced(po);
  }
  const invoicedPct = totalPoAmount > 0 ? Math.min(100, (totalInvoiced / totalPoAmount) * 100) : 0;
  return {
    totalPoAmount,
    totalInvoiced,
    invoicedPct,
    poCount: pos.length
  };
}

const MATERIAL_KPIS = [
  { key: 'poles', title: 'Wooden poles', cardClass: 'kpi-card--poles', valueClass: 'blue', accent: '#3b82f6' },
  { key: 'cables', title: 'Cables', cardClass: 'kpi-card--cables', valueClass: 'orange', accent: '#f97316' },
  { key: 'fittings', title: 'Fittings', cardClass: 'kpi-card--fittings', valueClass: 'red', accent: '#ef4444' },
  { key: 'transformers', title: 'Transformers', cardClass: 'kpi-card--transformers', valueClass: 'green', accent: '#22c55e' },
  { key: 'meters', title: 'Meters', cardClass: 'kpi-card--meters', valueClass: 'purple', accent: '#8b5cf6' },
  { key: 'others', title: 'Others', cardClass: 'kpi-card--others', valueClass: 'cyan', accent: '#06b6d4' },
  { key: 'fleet', title: 'Fleet', cardClass: 'kpi-card--fleet', valueClass: 'amber', accent: '#f59e0b' },
  { key: 'transportation', title: 'Transportation', cardClass: 'kpi-card--transportation', valueClass: 'emerald', accent: '#10b981' }
];

/** Material categories shown in the inventory summary (matches MaterialInventory page). */
const INVENTORY_CATEGORY_DEFS = [
  { name: 'Wooden poles', color: '#0C2D61' },
  { name: 'Cables', color: '#f97316' },
  { name: 'Fittings', color: '#ef4444' },
  { name: 'Transformers', color: '#22c55e' },
  { name: 'Meters', color: '#8b5cf6' }
];

const INVENTORY_CATEGORY_COLOR_FALLBACK = '#06b6d4';

function colorForInventoryCategory(name) {
  const found = INVENTORY_CATEGORY_DEFS.find(
    (c) => c.name.toLowerCase() === String(name || '').toLowerCase()
  );
  return found ? found.color : INVENTORY_CATEGORY_COLOR_FALLBACK;
}

/** Plain number formatter for material quantities (no currency). */
function formatQty(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function PoExpandRowsSkeleton({ count = 5, supplier = false }) {
  return Array.from({ length: count }, (_, i) => (
    <li key={`po-skel-${i}`} className="po-summary-expand__row dash-skel-row" aria-hidden>
      <div
        className={`po-summary-expand__row-main${supplier ? ' po-summary-expand__row-main--supplier' : ''}`}
      >
        <Skeleton className="h-4 w-4 shrink-0 rounded" />
        {!supplier ? <Skeleton className="h-2.5 w-2.5 shrink-0 rounded-full" /> : null}
        <Skeleton className={`h-3.5 ${supplier ? 'w-[55%]' : 'w-[38%]'}`} />
        <Skeleton className="ml-auto h-3.5 w-10" />
        <Skeleton className="h-3.5 w-24" />
      </div>
      <div className="po-summary-expand__row-track dash-skel-track">
        <Skeleton className="h-full w-full rounded-full" />
      </div>
    </li>
  ));
}

function InvExpandRowsSkeleton({ count = 5 }) {
  return Array.from({ length: count }, (_, i) => (
    <li key={`inv-skel-${i}`} className="inv-lot-expand__row dash-skel-row" aria-hidden>
      <div className="inv-lot-expand__row-main">
        <Skeleton className="h-4 w-4 shrink-0 rounded" />
        <Skeleton className="h-2.5 w-2.5 shrink-0 rounded-full" />
        <Skeleton className="h-3.5 w-[32%]" />
        <Skeleton className="h-3 w-14" />
        <Skeleton className="ml-auto h-3.5 w-10" />
      </div>
      <div className="inv-lot-expand__amounts">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-20" />
      </div>
      <div className="inv-lot-expand__row-track dash-skel-track">
        <Skeleton className="h-full w-full rounded-full" />
      </div>
    </li>
  ));
}

/**
 * Inventory summary card for a single LOT — Material Inventory ordered vs in-stock.
 */
function InventoryLotPanel({ title, region, summary, loading, accent }) {
  const [open, setOpen] = useState(false);
  const panelId = `inv-lot-${region.toLowerCase().replace(/\s+/g, '-')}`;
  const totals = summary?.totals || { items: 0, boqQty: 0, qtyDelivered: 0, boqBalanceQty: 0 };
  const totalOrdered = totals.boqQty;
  const totalInventory = totals.qtyDelivered;
  const totalPending = totals.boqBalanceQty;
  const overallPct = totalOrdered > 0
    ? Math.min(100, (totalInventory / totalOrdered) * 100)
    : 0;

  const rows = INVENTORY_CATEGORY_DEFS.map((def) => {
    const found = (summary?.byCategory || []).find(
      (c) => c.category.toLowerCase() === def.name.toLowerCase()
    );
    return {
      category: def.name,
      color: def.color,
      items: found ? found.items : 0,
      ordered: found ? found.boqQty : 0,
      inventory: found ? found.qtyDelivered : 0,
      percent: found ? found.deliveredPercent : 0
    };
  });

  const extras = (summary?.byCategory || [])
    .filter(
      (c) => !INVENTORY_CATEGORY_DEFS.some(
        (d) => d.name.toLowerCase() === c.category.toLowerCase()
      )
    )
    .map((c) => ({
      category: c.category,
      color: colorForInventoryCategory(c.category),
      items: c.items,
      ordered: c.boqQty,
      inventory: c.qtyDelivered,
      percent: c.deliveredPercent
    }));

  const allRows = [...rows, ...extras];
  const activeCategories = allRows.filter((r) => (r.ordered || 0) > 0).length;

  return (
    <Card
      className={`inv-lot-card ${open ? 'inv-lot-card--open' : ''}`}
      style={{ '--lot-accent': accent }}
    >
      <button
        type="button"
        className="inv-lot-card__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={`${panelId}-breakdown`}
        id={`${panelId}-trigger`}
      >
        <CardHeader className="inv-lot-card__header">
          <div className="inv-lot-card__head">
            <div className="inv-lot-card__title-block">
              <div className="inv-lot-card__title-row">
                <span className="inv-lot-card__icon" aria-hidden>
                  <MapPin />
                </span>
                <div>
                  <CardTitle className="inv-lot-card__title">{title}</CardTitle>
                  <p className="inv-lot-card__subtitle">
                    {region} · click to {open ? 'collapse' : 'expand'} categories
                  </p>
                </div>
              </div>
            </div>
            <div className="inv-lot-card__head-right">
              {loading ? (
                <Skeleton className="h-5 w-16 rounded-full" />
              ) : (
                <Badge variant="secondary" className="inv-lot-card__badge">
                  {`${totals.items} item${totals.items === 1 ? '' : 's'}`}
                </Badge>
              )}
              <ChevronDown className="inv-lot-card__chevron" aria-hidden />
            </div>
          </div>
        </CardHeader>

        <CardContent className="inv-lot-card__body">
          <div className="inv-lot-card__metrics">
            <div className="inv-lot-metric">
              <span className="inv-lot-metric__label">In inventory</span>
              <span className="inv-lot-metric__value">
                {loading ? <Skeleton className="h-6 w-20" /> : formatQty(totalInventory)}
              </span>
            </div>
            <div className="inv-lot-metric">
              <span className="inv-lot-metric__label">PO quantity</span>
              <span className="inv-lot-metric__value">
                {loading ? <Skeleton className="h-6 w-20" /> : formatQty(totalOrdered)}
              </span>
            </div>
            <div className="inv-lot-metric inv-lot-metric--pct">
              <span className="inv-lot-metric__label">Fulfilled</span>
              <span className="inv-lot-metric__value inv-lot-metric__value--pct">
                {loading ? <Skeleton className="h-6 w-14" /> : `${overallPct.toFixed(1)}%`}
              </span>
            </div>
          </div>

          <div className="inv-lot-progress-wrap">
            <div className="inv-lot-progress-meta">
              <span>Inventory vs ordered</span>
              <span>
                {loading ? (
                  <Skeleton className="inline-block h-3.5 w-28" />
                ) : (
                  `Pending ${formatQty(totalPending)} units`
                )}
              </span>
            </div>
            <div
              className={`inv-lot-progress${loading ? ' dash-skel-progress' : ''}`}
              aria-hidden="true"
            >
              {loading ? (
                <Skeleton className="h-full w-full rounded-full" />
              ) : (
                <div
                  className="inv-lot-progress__fill"
                  style={{ width: `${overallPct}%` }}
                />
              )}
            </div>
          </div>
        </CardContent>
      </button>

      {open && (
        <div
          className="inv-lot-expand"
          id={`${panelId}-breakdown`}
          role="region"
          aria-labelledby={`${panelId}-trigger`}
        >
          <div className="inv-lot-expand__head">
            <div className="inv-lot-expand__title-row">
              <span className="inv-lot-expand__icon" aria-hidden>
                <Package />
              </span>
              <div>
                <h3 className="inv-lot-expand__heading">By category</h3>
                <p className="inv-lot-expand__hint">PO qty vs inventory · material groups</p>
              </div>
            </div>
            <Badge variant="secondary" className="inv-lot-expand__count">
              {loading ? <Skeleton className="h-3.5 w-6" /> : activeCategories}
            </Badge>
          </div>
          <ul className="inv-lot-expand__list">
            {loading ? (
              <InvExpandRowsSkeleton count={INVENTORY_CATEGORY_DEFS.length} />
            ) : (
              allRows.map((row, idx) => {
                const isEmpty = (row.ordered || 0) === 0;
                const pct = row.percent || 0;
                return (
                  <li
                    key={row.category}
                    className={`inv-lot-expand__row${isEmpty ? ' inv-lot-expand__row--empty' : ''}`}
                  >
                    <div className="inv-lot-expand__row-main">
                      <span className="inv-lot-expand__rank">{idx + 1}</span>
                      <span
                        className="inv-lot-expand__dot"
                        style={{ background: row.color }}
                        aria-hidden
                      />
                      <span className="inv-lot-expand__name">{row.category}</span>
                      <span className="inv-lot-expand__items">
                        {row.items} item{row.items === 1 ? '' : 's'}
                      </span>
                      <span
                        className="inv-lot-expand__pct"
                        style={isEmpty ? undefined : { color: row.color }}
                      >
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                    <div className="inv-lot-expand__amounts">
                      <span>
                        <span className="inv-lot-expand__amount-label">PO Qty</span>
                        {formatQty(row.ordered)}
                      </span>
                      <span>
                        <span className="inv-lot-expand__amount-label inv-lot-expand__amount-label--inv">
                          Inventory
                        </span>
                        {formatQty(row.inventory)}
                      </span>
                    </div>
                    <div className="inv-lot-expand__row-track">
                      <span
                        className="inv-lot-expand__bar"
                        style={{
                          width: `${Math.min(100, pct)}%`,
                          background: row.color,
                        }}
                      />
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** Category rows sorted by PO amount desc; % of total PO amount across all POs. */
function buildCategoryDistribution(materialStats, totalPoAmount) {
  const rows = MATERIAL_KPIS.map((m) => ({
    key: m.key,
    title: m.title,
    amount: materialStats[m.key].poAmount,
    accent: m.accent,
  }));
  const bucketSum = rows.reduce((s, r) => s + r.amount, 0);
  const remainder = Math.max(0, totalPoAmount - bucketSum);
  if (remainder > 0.5) {
    rows.push({
      key: '_other',
      title: 'Other / unclassified',
      amount: remainder,
      accent: '#94a3b8',
    });
  }
  return rows
    .filter((r) => r.amount > 0)
    .map((r) => ({
      ...r,
      pct: totalPoAmount > 0 ? (r.amount / totalPoAmount) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

/** Supplier rows sorted by PO amount desc; % of total PO amount. */
function buildSupplierDistribution(pos, totalPoAmount) {
  const map = new Map();
  for (const po of pos) {
    const raw = (po.supplierName || '').trim() || 'Unknown supplier';
    const key = raw === 'Unknown supplier' ? '__unknown__' : normalizeSupplierNameForMatching(raw);
    const amt = rawPoAmount(po);
    const label = raw === 'Unknown supplier'
      ? 'Unknown supplier'
      : displayNameForCanonicalSupplier(key, raw);
    const cur = map.get(key) || { amount: 0, name: label };
    cur.amount += amt;
    cur.name = label;
    map.set(key, cur);
  }
  return [...map.values()]
    .map((v) => ({
      name: v.name,
      amount: v.amount,
      pct: totalPoAmount > 0 ? (v.amount / totalPoAmount) * 100 : 0
    }))
    .filter((r) => r.amount > 0)
    .sort((a, b) => b.amount - a.amount);
}

function Dashboard() {
  const session = useAuthSession();
  const lockedLotFilter = getLockedLotFilter(session);
  const isLot10Only = Boolean(lockedLotFilter);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poLoading, setPoLoading] = useState(true);
  const [poError, setPoError] = useState(null);
  const [poSummaryOpen, setPoSummaryOpen] = useState(false);
  const [inventoryRows, setInventoryRows] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(SHOW_MATERIAL_INVENTORY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPoLoading(true);
      setPoError(null);
      try {
        const list = await getAllPurchaseOrders();
        if (!cancelled) setPurchaseOrders(list);
      } catch (e) {
        if (!cancelled) setPoError(e.message || 'Failed to load purchase orders');
      } finally {
        if (!cancelled) setPoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!SHOW_MATERIAL_INVENTORY) return undefined;
    let cancelled = false;
    (async () => {
      setInventoryLoading(true);
      try {
        const rows = await loadMaterialInventoryRows();
        if (!cancelled) setInventoryRows(rows || []);
      } catch (err) {
        console.warn('Failed to load material inventory for dashboard summary:', err);
        if (!cancelled) setInventoryRows([]);
      } finally {
        if (!cancelled) setInventoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visiblePurchaseOrders = useMemo(() => {
    if (!isLot10Only) return purchaseOrders;
    return purchaseOrders.filter((po) => isLot10(po.lot));
  }, [purchaseOrders, isLot10Only]);

  const inventorySummary = useMemo(
    () => summarizeMaterialRowsByLot(inventoryRows),
    [inventoryRows]
  );

  const { global: materialStats } = useMemo(
    () => aggregatePurchaseOrders(visiblePurchaseOrders),
    [visiblePurchaseOrders]
  );

  const allPoTotals = useMemo(
    () => aggregateAllPoTotals(visiblePurchaseOrders),
    [visiblePurchaseOrders]
  );

  const categoryDistribution = useMemo(
    () => buildCategoryDistribution(materialStats, allPoTotals.totalPoAmount),
    [materialStats, allPoTotals.totalPoAmount]
  );

  const supplierDistribution = useMemo(
    () => buildSupplierDistribution(visiblePurchaseOrders, allPoTotals.totalPoAmount),
    [visiblePurchaseOrders, allPoTotals.totalPoAmount]
  );


  return (
    <div className="financial-dashboard">
      <div className="dashboard-scroll">
        <div className="dashboard-panel">
          <DashboardAppBar />

          <div className="dashboard-panel-body">
      <section className="dashboard-intro" aria-label="Dashboard overview">
        <div className="dashboard-intro-text">
          <h1 className="dashboard-intro-title">Operations overview</h1>
          <p className="dashboard-intro-subtitle">
            {isLot10Only
              ? 'Purchase order amounts vs invoiced — LOT 10 (Manyara) only'
              : 'Purchase order amounts vs invoiced across all lots'}
            {poError ? (
              <span className="dashboard-intro-error"> · Could not refresh POs: {poError}</span>
            ) : null}
          </p>
        </div>
      </section>

      <section className="po-summary-section" aria-label="Totals for all purchase orders">
        <Card className={`po-summary-card ${poSummaryOpen ? 'po-summary-card--open' : ''}`}>
          <button
            type="button"
            className="po-summary-card__trigger"
            onClick={() => setPoSummaryOpen((o) => !o)}
            aria-expanded={poSummaryOpen}
            aria-controls="po-summary-breakdown"
            id="po-summary-trigger"
          >
            <CardHeader className="po-summary-card__header">
              <div className="po-summary-card__head">
                <div className="po-summary-card__title-block">
                  <CardTitle className="po-summary-card__title">All purchase orders</CardTitle>
                  <p className="po-summary-card__subtitle">
                    Portfolio totals across every PO · click to {poSummaryOpen ? 'collapse' : 'expand'} breakdown
                  </p>
                </div>
                <div className="po-summary-card__head-right">
                  {poLoading ? (
                    <Skeleton className="h-5 w-16 rounded-full" />
                  ) : (
                    <Badge variant="secondary" className="po-summary-card__badge">
                      {`${allPoTotals.poCount.toLocaleString('en-US')} PO${allPoTotals.poCount === 1 ? '' : 's'}`}
                    </Badge>
                  )}
                  <ChevronDown className="po-summary-card__chevron" aria-hidden />
                </div>
              </div>
            </CardHeader>

            <CardContent className="po-summary-card__body">
              <div className="po-summary-card__metrics" aria-busy={poLoading}>
                <div className="po-summary-metric">
                  <span className="po-summary-metric__label">Total PO amount</span>
                  <span className="po-summary-metric__value">
                    {poLoading ? (
                      <Skeleton className="h-6 w-36 max-w-full" />
                    ) : (
                      formatCurrencyLong(allPoTotals.totalPoAmount)
                    )}
                  </span>
                </div>
                <div className="po-summary-metric">
                  <span className="po-summary-metric__label">Total invoiced</span>
                  <span className="po-summary-metric__value">
                    {poLoading ? (
                      <Skeleton className="h-6 w-36 max-w-full" />
                    ) : (
                      formatCurrencyLong(allPoTotals.totalInvoiced)
                    )}
                  </span>
                </div>
                <div className="po-summary-metric po-summary-metric--pct">
                  <span className="po-summary-metric__label">Invoiced vs PO</span>
                  <span className="po-summary-metric__value po-summary-metric__value--pct">
                    {poLoading ? (
                      <Skeleton className="h-6 w-14" />
                    ) : (
                      `${allPoTotals.invoicedPct.toFixed(1)}%`
                    )}
                  </span>
                </div>
              </div>

              <div className="po-summary-progress-wrap">
                <div className="po-summary-progress-meta">
                  <span>Invoicing progress</span>
                  <span>
                    {poLoading ? (
                      <Skeleton className="inline-block h-3.5 w-12" />
                    ) : (
                      `${allPoTotals.invoicedPct.toFixed(1)}%`
                    )}
                  </span>
                </div>
                <div
                  className={`po-summary-progress${poLoading ? ' dash-skel-progress' : ''}`}
                  aria-hidden="true"
                >
                  {poLoading ? (
                    <Skeleton className="h-full w-full rounded-full" />
                  ) : (
                    <div
                      className="po-summary-progress__fill"
                      style={{
                        width: `${Math.min(100, allPoTotals.invoicedPct)}%`,
                      }}
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </button>

          {poSummaryOpen && (
            <div
              className="po-summary-expand"
              id="po-summary-breakdown"
              role="region"
              aria-labelledby="po-summary-trigger"
            >
              <div className="po-summary-expand__grid">
                <div className="po-summary-expand__col">
                  <div className="po-summary-expand__col-head">
                    <div className="po-summary-expand__col-title">
                      <span className="po-summary-expand__icon po-summary-expand__icon--category">
                        <Layers2 aria-hidden />
                      </span>
                      <div>
                        <h3 className="po-summary-expand__heading">Category share</h3>
                        <p className="po-summary-expand__hint">By PO amount · highest to lowest</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="po-summary-expand__count">
                      {poLoading ? <Skeleton className="h-3.5 w-6" /> : categoryDistribution.length}
                    </Badge>
                  </div>
                  <ul className="po-summary-expand__list">
                    {poLoading ? (
                      <PoExpandRowsSkeleton count={6} />
                    ) : categoryDistribution.length === 0 ? (
                      <li className="po-summary-expand__empty">No data</li>
                    ) : (
                      categoryDistribution.map((row, idx) => (
                        <li key={row.key} className="po-summary-expand__row">
                          <div className="po-summary-expand__row-main">
                            <span className="po-summary-expand__rank">{idx + 1}</span>
                            <span
                              className="po-summary-expand__dot"
                              style={{ background: row.accent || '#94a3b8' }}
                              aria-hidden
                            />
                            <span className="po-summary-expand__name">{row.title}</span>
                            <span className="po-summary-expand__pct">{row.pct.toFixed(1)}%</span>
                            <span className="po-summary-expand__amt">{formatCurrencyLong(row.amount)}</span>
                          </div>
                          <div className="po-summary-expand__row-track">
                            <span
                              className="po-summary-expand__bar"
                              style={{
                                width: `${Math.min(100, row.pct)}%`,
                                background: row.accent || '#2563eb',
                              }}
                            />
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>

                <div className="po-summary-expand__col">
                  <div className="po-summary-expand__col-head">
                    <div className="po-summary-expand__col-title">
                      <span className="po-summary-expand__icon po-summary-expand__icon--supplier">
                        <Users aria-hidden />
                      </span>
                      <div>
                        <h3 className="po-summary-expand__heading">Suppliers</h3>
                        <p className="po-summary-expand__hint">By PO amount · highest to lowest</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="po-summary-expand__count">
                      {poLoading ? <Skeleton className="h-3.5 w-6" /> : supplierDistribution.length}
                    </Badge>
                  </div>
                  <ul className="po-summary-expand__list">
                    {poLoading ? (
                      <PoExpandRowsSkeleton count={6} supplier />
                    ) : supplierDistribution.length === 0 ? (
                      <li className="po-summary-expand__empty">No suppliers</li>
                    ) : (
                      supplierDistribution.map((row, idx) => (
                        <li key={`${row.name}-${idx}`} className="po-summary-expand__row">
                          <div className="po-summary-expand__row-main po-summary-expand__row-main--supplier">
                            <span className="po-summary-expand__rank">{idx + 1}</span>
                            <span className="po-summary-expand__name" title={row.name}>
                              {row.name}
                            </span>
                            <span className="po-summary-expand__pct">{row.pct.toFixed(1)}%</span>
                            <span className="po-summary-expand__amt">{formatCurrencyLong(row.amount)}</span>
                          </div>
                          <div className="po-summary-expand__row-track">
                            <span
                              className="po-summary-expand__bar po-summary-expand__bar--supplier"
                              style={{ width: `${Math.min(100, row.pct)}%` }}
                            />
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </Card>
      </section>

      {/* First Row - KPI Cards */}
      <div className="dashboard-row dashboard-row-kpi" aria-busy={poLoading}>
        {MATERIAL_KPIS.map((m) => {
          const stat = materialStats[m.key];
          const pct = stat.poAmount > 0 ? Math.min(100, (stat.invoiced / stat.poAmount) * 100) : 0;
          return (
            <div key={m.key} className={`kpi-card ${m.cardClass}`}>
              <div className="kpi-card-head">
                <h3>{m.title}</h3>
                {poLoading ? (
                  <Skeleton className="h-5 w-8 rounded-full" />
                ) : (
                  <span
                    className="kpi-po-count"
                    title="Number of purchase orders in this category"
                  >
                    {stat.count}
                  </span>
                )}
              </div>
              <div className="kpi-content">
                <div className={`kpi-value kpi-po-amount ${m.valueClass}`}>
                  {poLoading ? (
                    <Skeleton className="h-7 w-[85%] max-w-[10rem]" />
                  ) : (
                    formatCurrencyLong(stat.poAmount)
                  )}
                </div>
                <div className="kpi-invoiced-row">
                  <span className="kpi-invoiced-label">Invoiced</span>
                  <span className="kpi-invoiced-value">
                    {poLoading ? (
                      <Skeleton className="h-4 w-28 ml-auto" />
                    ) : (
                      <>
                        {formatCurrencyLong(stat.invoiced)}
                        {stat.poAmount > 0 ? (
                          <span className="kpi-invoiced-pct"> ({pct.toFixed(1)}%)</span>
                        ) : null}
                      </>
                    )}
                  </span>
                </div>
                <div
                  className={`kpi-progress-track${poLoading ? ' dash-skel-progress' : ''}`}
                  aria-hidden
                >
                  {poLoading ? (
                    <Skeleton className="h-full w-full rounded-full" />
                  ) : (
                    <div
                      className="kpi-progress-fill"
                      style={{ width: `${pct}%`, background: m.accent }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {SHOW_MATERIAL_INVENTORY ? (
      <section
        className="inv-summary-section"
        aria-label="Inventory summary by LOT"
        aria-busy={inventoryLoading}
      >
        <header className="inv-summary-section__head">
          <h2 className="inv-summary-section__title">Inventory summary by LOT</h2>
          <p className="inv-summary-section__subtitle">
            Ordered PO quantity vs inventory on hand — Material Inventory
          </p>
        </header>
        <div className="inv-summary-grid">
          {!isLot10Only ? (
          <InventoryLotPanel
            title="LOT 8"
            region="Morogoro"
            summary={inventorySummary.lot8}
            loading={inventoryLoading}
            accent="#0d9488"
          />
          ) : null}
          <InventoryLotPanel
            title="LOT 10"
            region="Manyara"
            summary={inventorySummary.lot10}
            loading={inventoryLoading}
            accent="#2563eb"
          />
        </div>
      </section>
      ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
